import { format } from 'date-fns';

import { TIME_PICKER_FORMAT } from './types';

/**
 * The quarter-hour slot list, and the one predicate that decides what is selectable.
 *
 * ── Why the generator and the predicate live in the same file ─────────────────────────────────
 *
 * Because a combobox has TWO ways in, and they must not be able to disagree. The popup offers a
 * list; the field accepts anything a user types. If the list were built one way and the typed entry
 * checked another, the field would eventually offer a slot it then refused, or refuse one it had
 * just offered — and neither failure is visible from either half on its own.
 *
 * So there is exactly one generator (`buildTimeSlots`) and exactly one predicate
 * (`isTimeSelectable`). The list is the generator filtered through the predicate
 * (`selectableTimeSlots`), and the blur parser in `TimePicker.tsx` calls the SAME predicate on the
 * minutes it parsed. Two call sites, one rule.
 *
 * ── KTD4: `min` is a TIME-OF-DAY bound, and this is the divergence from `DatePicker` ──────────
 *
 * `DatePicker` compares a typed entry against `startOfDay(min)`, so a mid-day `min` means the whole
 * of its day. That is correct there and it is forced: `react-day-picker` matches `before`/`after`
 * with `differenceInCalendarDays`, ignoring time of day entirely, so comparing typed entries
 * against the raw instant would make the grid and the keyboard disagree.
 *
 * Copying it here would make `min` do NOTHING. Every slot this file generates shares one day, so
 * `startOfDay(min)` is at or before all 96 of them and nothing would ever be filtered out. The
 * wizard's end-time field, whose entire job is to forbid an end before its start, would silently
 * stop forbidding anything.
 *
 * Comparing raw INSTANTS is equally wrong, and for a reason specific to the one call site. The
 * wizard hands the end field `addMinutes(startTime, 15)` while the value being edited sits on the
 * occurrence's own date, and the two `Date`s are not required to share a day — `useEventDates`
 * re-bases them onto `eventDate` on its own schedule, so mid-edit they routinely differ. An instant
 * comparison would then reject the entire list on one render and accept all of it on the next,
 * depending on which side of midnight the two happened to land.
 *
 * **The rule, stated once:** a time is selectable when its minutes since local midnight are
 * GREATER THAN OR EQUAL TO `min`'s minutes since local midnight. The calendar day of `min` is
 * ignored. Inclusive at `min`, exclusive below it.
 *
 * Two consequences, recorded rather than discovered later:
 *
 *   - A `min` that is not on a quarter boundary still works — `09:07` drops `09:00` and keeps
 *     `09:15` — because the predicate compares minutes, not slot indices.
 *   - A `min` that has CROSSED MIDNIGHT collapses. A start at `23:50` gives the end field a `min`
 *     of `00:05` the next day, which this rule reads as five past midnight and which therefore
 *     permits almost the whole day. That is a real limit of a field that holds a time and not an
 *     instant, and the honest fix is a bound the caller expresses as a time of day rather than as a
 *     `Date`. It is left as-is because the one call site cannot reach it: the wizard's occurrence
 *     rows put start and end on the same date, and the form rejects an end before a start
 *     separately.
 */

/** Fifteen minutes. The legacy field's step, and nothing in HNS-418 asks to change it. */
export const TIME_SLOT_STEP_MINUTES = 15;

/** Twenty-four hours, in minutes. Named so `MINUTES_IN_DAY / STEP` reads as the slot count. */
export const MINUTES_IN_DAY = 24 * 60;

/**
 * How many slots a day holds — 96. Exported so the component can size its list without
 * recomputing the arithmetic, and so a test can assert the count against the derivation rather than
 * against a literal typed twice.
 */
export const TIME_SLOT_COUNT = MINUTES_IN_DAY / TIME_SLOT_STEP_MINUTES;

/**
 * One row of the list.
 *
 * `minutes` is the canonical identity and `label` is the rendering, rather than the label being
 * re-parsed wherever the minutes are wanted. Base UI matches an item by its `value`, which is the
 * label string, so the label must be unique across the list — with a 15-minute step over one day it
 * is, and `TIME_SLOT_COUNT` distinct labels is what `slots.test.ts` asserts.
 */
export type TimeSlot = {
  /** Minutes since local midnight, `0` to `1425`. */
  minutes: number;
  /** The row's text, and the string the field shows once the row is chosen. */
  label: string;
};

/** Minutes since local midnight. Seconds and anything finer are discarded, not rounded. */
export const minutesOfDay = (value: Date) => value.getHours() * 60 + value.getMinutes();

/**
 * `base`'s DAY carrying `minutes`'s time, with seconds and milliseconds zeroed.
 *
 * The zeroing is what makes two committed values comparable: a field that holds `09:30:00.000` on
 * one commit and `09:30:41.812` on the next would report a change nobody made. The day is taken
 * from `base` rather than from today, so a caller keeping its times on a particular date keeps them
 * there — the field edits the time and touches nothing else.
 */
export const atTimeOfDay = (base: Date, minutes: number) => {
  const next = new Date(base.getTime());

  next.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);

  return next;
};

/**
 * Every quarter-hour of a day, in order, from `12:00 AM` to `11:45 PM`.
 *
 * Built from an arbitrary fixed date rather than from `new Date()`, so the labels are identical on
 * every render and in every test run. The date itself never escapes: only `label` does, and the
 * committed `Date` is built by `atTimeOfDay` from the caller's own value.
 *
 * It is a function rather than a frozen constant because `TIME_SLOTS` below is the shared instance
 * and this is the derivation — a test asserting the count needs to run the derivation, not read a
 * cached array that might have been mutated by a consumer.
 */
export const buildTimeSlots = (): TimeSlot[] => {
  // Midday on an arbitrary date with no DST transition anywhere near it. Midnight would be the
  // obvious choice and is the wrong one: in a zone that springs forward at 00:00 there is no
  // 00:00, and `setHours` would silently shift the whole list by an hour.
  const anchor = new Date(2026, 0, 15, 12, 0, 0, 0);

  return Array.from({ length: TIME_SLOT_COUNT }, (_unused, index) => {
    const minutes = index * TIME_SLOT_STEP_MINUTES;

    return { minutes, label: format(atTimeOfDay(anchor, minutes), TIME_PICKER_FORMAT) };
  });
};

/**
 * The 96 slots, built once.
 *
 * Module scope rather than a `useMemo`, because the list never varies: `min` filters it, it does not
 * change it. A stable array identity also keeps Base UI's own filtering from re-deriving on every
 * render of every field on the page.
 */
export const TIME_SLOTS: readonly TimeSlot[] = buildTimeSlots();

/**
 * The caller's `min` reduced to the only part of it that means anything here.
 *
 * This ONE call is where a `Date` becomes a time of day, and it is deliberately the only place in
 * the component where that conversion happens. Everything downstream takes minutes, so nothing
 * downstream is even capable of comparing instants by accident — KTD4 becomes a fact about the
 * types rather than a rule someone has to remember. `null` is "unbounded", and it is distinct from
 * `0`: an unbounded field is not a field bounded at midnight, even though the two offer the same
 * rows today.
 *
 * It is also what makes the component's memoisation honest: the one call site rebuilds
 * `addMinutes(startTime, 15)` on every render, so a `Date` is a new object each time and a number
 * is not.
 */
export const boundMinutes = (min?: Date): number | null => (min === undefined ? null : minutesOfDay(min));

/**
 * THE predicate. See the KTD4 note at the top of this file for why it compares time of day.
 *
 * Both arguments are minutes since midnight, so the blur parser can ask about an entry it has not
 * turned into a `Date` yet — and so the question this answers is unambiguously about a time.
 */
export const isTimeSelectable = (minutes: number, minMinutes: number | null) =>
  minMinutes === null || minutes >= minMinutes;

/**
 * The list the popup renders: the generator, filtered through the predicate.
 *
 * Returns a new array, so the caller memoises it — see `TimePicker.tsx`, which can key on the
 * number this takes precisely because it is a number.
 */
export const selectableTimeSlots = (minMinutes: number | null): TimeSlot[] =>
  TIME_SLOTS.filter(({ minutes }) => isTimeSelectable(minutes, minMinutes));

/**
 * Whether a slot label matches what the user has typed so far.
 *
 * Substring match, case-insensitive, like the popup's default, with one addition: a leading zero on
 * the hour is ignored. Labels are unpadded (`9:30 AM`), but people still type the padded form, and
 * `09:3` would otherwise match nothing and empty the list until the zero was deleted. Blur-time
 * parsing already accepts `09:30` and `9:30`, so the live filter agrees with it.
 */
export const matchesTimeQuery = (label: string, query: string) =>
  label.toLowerCase().includes(
    query
      .trim()
      .toLowerCase()
      .replace(/^0+(?=\d)/, '')
  );
